import { Injectable } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { switchMap, catchError, of, withLatestFrom, map, tap, mergeMap, distinctUntilChanged, filter } from 'rxjs'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import * as EventTicketPackagesActions from './event-ticket-packages.actions'
import { NavigationExtras, Router } from '@angular/router'
import { EventTicketPackagesApiService } from './event-ticket-packages-api.service'
import { EventTicketPackagesFacade } from './event-ticket-packages.facade'
import { EventTicketsConstants } from 'libs/gb-front/event-tickets/feature/src/lib/event-tickets-constants'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { Action } from '@ngrx/store'
import { EventTicketPackageEntity } from './event-ticket-packages.models'
import { NotFoundConstants } from 'libs/gb-front/not-found/feature/src/lib/gb-front-not-found-feature.module'

@Injectable()
export class EventTicketPackagesEffects {
  loadEventTicketPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.loadEventTicketPackages),
      tap(() => this.loaderService.setLoading(LoaderActionTypeEnum.LoadEventTicketPackages)),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.eventTicketPackagesFacade.eventTicketPackages$),
      switchMap(([action, urlName, eventTicketPackages]) => {
        // set offset to action.offset or to length of eventTicketPackages
        const offset = action.offset ?? eventTicketPackages?.length ?? 0

        return this.eventTicketPackagesApiService.getEventTicketPackages(urlName, offset, action.limit).pipe(
          mergeMap((response) => [EventTicketPackagesActions.loadEventTicketPackagesSuccess({ response, offset })]),
          catchError((error) => of(EventTicketPackagesActions.loadEventTicketPackagesFailure({ error }))),
        )
      }),
    ),
  )

  resyncEventTicketPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.resyncEventTicketPackages),
      distinctUntilChanged((prev, curr) => prev.offset === curr.offset && !curr.onFocus),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.eventTicketPackagesFacade.stalePages$),
      filter(([action, , stalePages]) => stalePages.includes(action.offset)),
      mergeMap(([action, urlName]) =>
        this.eventTicketPackagesApiService.getEventTicketPackages(urlName, action.offset).pipe(
          switchMap((response) => [
            EventTicketPackagesActions.removePageFromStale({
              page: action.offset,
            }),
            EventTicketPackagesActions.loadEventTicketPackagesSuccess({
              response,
              offset: action.offset,
            }),
          ]),
          catchError((error) =>
            of(
              EventTicketPackagesActions.loadEventTicketPackagesFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  updateCountOnLoadEventTicketPackagesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.loadEventTicketPackagesSuccess),
      map((action) => EventTicketPackagesActions.updateCount({ count: action.response.count })),
    ),
  )

  /**
   * On loadEventTicketPackagesSuccess and order hash not changed:
   * - Update event ticket packages
   */
  handleOrderUnchangedOnLoadEventTicketPackagesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.loadEventTicketPackagesSuccess),
      withLatestFrom(this.eventTicketPackagesFacade.order$),
      filter(([action, order]) => action.response.order_hash === order), // order hash not changed
      map(([action]) =>
        EventTicketPackagesActions.upsertEventTicketPackages({
          eventTicketPackages: action.response.results,
        }),
      ),
    ),
  )

  /**
   * On loadEventTicketPackagesSuccess and order hash changed:
   * - Update order hash
   * - Clear event ticket packages
   * - Update or reload first page
   */
  handleOrderChangedOnLoadEventTicketPackagesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.loadEventTicketPackagesSuccess),
      withLatestFrom(this.eventTicketPackagesFacade.order$),
      filter(([action, order]) => action.response.order_hash !== order), // order hash changed
      switchMap(([action]) => [
        EventTicketPackagesActions.updateOrderHash({
          order: action.response.order_hash,
        }),
        EventTicketPackagesActions.clearEventTicketPackages(),
        // if first page load, update with response otherwise reload first page
        'offset' in action && action.offset === 0
          ? EventTicketPackagesActions.upsertEventTicketPackages({
              eventTicketPackages: action.response.results,
            })
          : EventTicketPackagesActions.loadEventTicketPackages(0),
      ]),
    ),
  )

  setLoadingComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventTicketPackagesActions.loadEventTicketPackagesSuccess,
          EventTicketPackagesActions.loadEventTicketPackagesFailure,
        ),
        tap(() => this.loaderService.setLoadingComplete(LoaderActionTypeEnum.LoadEventTicketPackages)),
      ),
    { dispatch: false },
  )

  syncEventTicketPackageOnSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.selectEventTicketPackage),
      withLatestFrom(this.eventTicketPackagesFacade.selectedEventTicketPackageId$),
      map(([_, selectedEventTicketPackageId]) =>
        EventTicketPackagesActions.syncEventTicketPackage({
          uuid: selectedEventTicketPackageId as string,
        }),
      ),
    ),
  )

  syncEventTicketPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.syncEventTicketPackage),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) =>
        this.eventTicketPackagesApiService.getEventTicketPackage(urlName, action.uuid).pipe(
          map((response) =>
            EventTicketPackagesActions.syncEventTicketPackageSuccess({
              eventTicketPackage: response,
            }),
          ),
          catchError((error) =>
            of(
              EventTicketPackagesActions.syncEventTicketPackageFailure({
                error,
                uuid: action.uuid,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  /**
   * On syncEventTicketPackageSuccess and order hash not changed:
   * - Update event ticket package
   */
  handleOrderUnchangedOnSyncEventTicketPackageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.syncEventTicketPackageSuccess),
      withLatestFrom(this.eventTicketPackagesFacade.order$),
      filter(
        ([action, order]) => action.eventTicketPackage.order_hash === order, // order hash not changed
      ),
      map(([action]) =>
        EventTicketPackagesActions.upsertEventTicketPackage({
          eventTicketPackage: action.eventTicketPackage,
        }),
      ),
    ),
  )

  /**
   * On syncEventTicketPackageSuccess and order hash changed:
   * - Update order hash
   * - Clear event ticket packages
   * - Reload first page
   */
  handleOrderChangedOnSyncEventTicketPackageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.syncEventTicketPackageSuccess),
      withLatestFrom(this.eventTicketPackagesFacade.order$),
      filter(
        ([action, order]) => action.eventTicketPackage.order_hash !== order, // order hash changed
      ),
      switchMap(([action]) => [
        EventTicketPackagesActions.updateOrderHash({
          order: action.eventTicketPackage.order_hash,
        }),
        EventTicketPackagesActions.clearEventTicketPackages(),
        EventTicketPackagesActions.loadEventTicketPackages(0),
      ]),
    ),
  )

  upsertEventTicketPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.upsertEventTicketPackage),
      withLatestFrom(this.eventTicketPackagesFacade.eventTicketPackages$),
      switchMap(([action, eventTicketPackages]) => {
        const isEventTicketPackageInState = this.isEventTicketPackageInState(
          eventTicketPackages,
          action.eventTicketPackage.uuid,
        )

        const actionToDispatch = isEventTicketPackageInState
          ? EventTicketPackagesActions.updateEventTicketPackage({
              update: {
                id: action.eventTicketPackage.uuid,
                changes: action.eventTicketPackage,
              },
            })
          : EventTicketPackagesActions.addEventTicketPackage({
              eventTicketPackage: {
                ...action.eventTicketPackage,
                notListLoaded: true,
              },
            })

        return [actionToDispatch]
      }),
    ),
  )

  deleteEventTicketPackageOn404$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventTicketPackagesActions.syncEventTicketPackageFailure),
      filter((action) => action.error.status === 404),
      withLatestFrom(this.eventTicketPackagesFacade.eventTicketPackages$),
      switchMap(([action, eventTicketPackages]) => {
        const actions: Action[] = []
        if (action.uuid && this.isEventTicketPackageInState(eventTicketPackages, action.uuid)) {
          actions.push(
            EventTicketPackagesActions.deleteEventTicketPackage({
              uuid: action.uuid,
            }),
          )
        }
        return actions
      }),
    ),
  )

  redirectToNotFoundOn404$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.syncEventTicketPackageFailure),
        filter((action) => action.error.status === 404),
        withLatestFrom(
          this.auctionFacade.auctionUrlName$,
          this.eventTicketPackagesFacade.selectedEventTicketPackageId$,
        ),
        tap(([action, urlName, selectedEventTicketPackageId]) => {
          // redirect only if the not-found ticket package is currently being viewed
          if (selectedEventTicketPackageId === action.uuid) {
            this.router.navigateByUrl(`/${urlName}/${NotFoundConstants.PAGE_NOT_FOUND}`)
          }
        }),
      ),
    { dispatch: false },
  )

  navigateToEventTicketPackage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTicketPackagesActions.navigateToEventTicketPackage),
        withLatestFrom(this.auctionFacade.auctionUrlName$),
        tap(([action, urlName]) => {
          const extras: NavigationExtras = {}

          if (action.backPath) {
            extras.queryParams = { backPath: action.backPath }
          }
          this.router.navigate([`/${urlName}/${EventTicketsConstants.EVENT_TICKETS}`, action.uuid], extras)
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private readonly actions$: Actions,
    private eventTicketPackagesApiService: EventTicketPackagesApiService,
    private eventTicketPackagesFacade: EventTicketPackagesFacade,
    private auctionFacade: AuctionFacade,
    private loaderService: GbLoaderService,
    private router: Router,
  ) {}

  private isEventTicketPackageInState(
    eventTicketPackages: EventTicketPackageEntity[],
    eventTicketPackageId: string,
  ): boolean {
    return eventTicketPackages.some((eventTicketPackage) => eventTicketPackage.uuid === eventTicketPackageId)
  }
}
